declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

let recaptchaLoaded = false
let recaptchaLoading = false
let loadedSiteKey: string | null = null

/**
 * Loads the Google reCAPTCHA v3 script with the site key
 * @param siteKey - Your reCAPTCHA site key (required for v3)
 */
function loadRecaptchaScript(siteKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // If no site key provided, reject
    if (!siteKey || siteKey.trim() === '') {
      reject(new Error('reCAPTCHA site key is required'))
      return
    }

    // If already loaded with the same site key, resolve immediately
    if (recaptchaLoaded && loadedSiteKey === siteKey) {
      resolve()
      return
    }

    // If loading with a different site key, wait for current load to complete
    if (recaptchaLoading && loadedSiteKey !== siteKey) {
      const checkInterval = setInterval(() => {
        if (recaptchaLoaded) {
          clearInterval(checkInterval)
          // If different site key, need to reload
          if (loadedSiteKey !== siteKey) {
            recaptchaLoaded = false
            loadRecaptchaScript(siteKey).then(resolve).catch(reject)
          } else {
            resolve()
          }
        }
      }, 100)
      return
    }

    // If already loaded but with different site key, reload
    if (recaptchaLoaded && loadedSiteKey !== siteKey) {
      recaptchaLoaded = false
      recaptchaLoading = false
      // Remove old script
      const oldScript = document.querySelector('script[src*="recaptcha"]')
      if (oldScript) {
        oldScript.remove()
      }
    }

    if (recaptchaLoading) {
      // Wait for existing load to complete
      const checkInterval = setInterval(() => {
        if (recaptchaLoaded) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 100)
      return
    }

    recaptchaLoading = true

    // Check if script already exists with correct site key
    const existingScript = document.querySelector(
      `script[src*="recaptcha"][src*="${siteKey}"]`,
    )
    if (existingScript) {
      recaptchaLoaded = true
      loadedSiteKey = siteKey
      recaptchaLoading = false
      resolve()
      return
    }

    const script = document.createElement('script')
    // For reCAPTCHA v3, the site key must be included in the script URL
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
    script.async = true
    script.defer = true
    script.onload = () => {
      recaptchaLoaded = true
      loadedSiteKey = siteKey
      recaptchaLoading = false
      resolve()
    }
    script.onerror = () => {
      recaptchaLoading = false
      reject(new Error('Failed to load reCAPTCHA script'))
    }
    document.head.appendChild(script)
  })
}

/**
 * Executes reCAPTCHA v3 and returns the token
 * @param siteKey - Your reCAPTCHA site key
 * @param action - The action name (e.g., 'signin', 'signup')
 * @returns Promise<string> - The reCAPTCHA token
 */
export async function executeRecaptcha(
  siteKey: string,
  action: string = 'submit',
): Promise<string> {
  // If no site key provided, return empty string (for development/testing)
  if (!siteKey || siteKey.trim() === '') {
    console.warn(
      'reCAPTCHA site key not configured. Skipping reCAPTCHA verification.',
    )
    return ''
  }

  await loadRecaptchaScript(siteKey)

  return new Promise((resolve, reject) => {
    if (!window.grecaptcha) {
      console.error('reCAPTCHA not loaded')
      reject(new Error('reCAPTCHA not loaded'))
      return
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey, { action })
        .then((token) => {
          resolve(token)
        })
        .catch((error) => {
          console.error('reCAPTCHA error:', error)
          reject(error)
        })
    })
  })
}

